import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUserWorkflowRuns,
  triggerUserWorkflow,
  retryWorkflowDispatch,
  retryWorkflowStep,
  confirmManualStep,
} from '../services/api/workflows.js';
import { useErrorToast } from './queryUtils';

// F9-007: keep this list aligned with backend `ACTIVE_WORKFLOW_RUN_STATUSES`
// in `workflowService.ts`. `dispatch_failed` is a recoverable state (the
// watchdog or a workflow-lock retry can flip it back to `dispatched`), so
// the FE must continue polling when a run lands there — otherwise the panel
// freezes on the failure flash and never lights up when recovery happens.
const ACTIVE_WORKFLOW_RUN_STATUSES = [
  'pending',
  'dispatched',
  'running',
  'dispatch_failed',
] as const;

export function useUserWorkflowRuns(userId: number) {
  const query = useQuery({
    queryKey: ['userWorkflowRuns', userId],
    queryFn: () => getUserWorkflowRuns(userId),
    enabled: userId > 0,
    refetchInterval: (query) => {
      const runs = query.state.data;
      const hasInFlight = runs?.some((r) =>
        (ACTIVE_WORKFLOW_RUN_STATUSES as readonly string[]).includes(r.status),
      );
      return hasInFlight ? 3000 : false;
    },
  });
  return query;
}

export function useTriggerWorkflow(userId: number) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: () => triggerUserWorkflow(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userWorkflowRuns', userId] });
    },
    onError: (err) => onError(err, 'Failed to trigger workflow'),
  });
}

export function useRetryDispatch(userId: number) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (runId: number) => retryWorkflowDispatch(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userWorkflowRuns', userId] });
    },
    onError: (err) => onError(err, 'Failed to retry dispatch'),
  });
}

export function useRetryStep(userId: number) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (stepId: number) => retryWorkflowStep(stepId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userWorkflowRuns', userId] });
    },
    onError: (err) => onError(err, 'Failed to retry step'),
  });
}

export function useConfirmManualStep(userId: number) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (stepId: number) => confirmManualStep(stepId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userWorkflowRuns', userId] });
    },
    onError: (err) => onError(err, 'Failed to confirm manual step'),
  });
}
